import { useState } from "react";
import {
  Activity,
  Cloud,
  Monitor,
  Smartphone,
  CheckCircle2,
  AlertCircle,
  BatteryCharging,
} from "lucide-react";
import { Badge, Button, SummaryMetrics } from "../../design-system";
import { type Customer } from "./console-model";
import "./system-network.css";
import { MachineDeviceSummary } from "./machine-device-summary";

export const machineVersionLabel = (version?: string | null) =>
  version?.trim() ? `v${version.trim().replace(/^v/, "")}` : "";

export type SystemExample =
  | "healthy"
  | "attention"
  | "empty"
  | "many"
  | "version-empty";
type Device = {
  serialTail: string;
  model: string | null;
  state: string;
  batteryPct: number | null;
  charging: boolean | null;
};
export type StatusMachine = {
  id: string;
  label: string;
  connected: boolean;
  lastSeenAt: string | null;
  version: string | null;
  readiness: string;
  pendingDeviceCount?: number;
  issues: string[];
  ai: {
    available: boolean;
    loginRequired: boolean;
    throttledUntil: string | null;
  };
  devices: {
    attached: number;
    ready: number;
    attention: number;
    list: Device[];
  };
  busy: boolean;
};
type Segment = { from: string; to: string; connected: boolean };
type Timeline = {
  from: string;
  to: string;
  hours: number;
  machines: {
    machineId: string;
    label: string;
    segments: Segment[];
    uptimePct: number | null;
    disconnects: number;
  }[];
};
const stamp = (hour: number) =>
  new Date(Date.UTC(2026, 9, 18, 5) + hour * 3600000).toISOString();
const time = (s: string | null) =>
  s
    ? new Date(s).toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "수신 기록 없음";
const issueText: Record<string, string> = {
  offline: "PC 전원과 인터넷 연결을 확인하세요.",
  device_attention: "디바이스에서 USB 디버깅 허용을 눌러 주세요.",
  no_device: "PC에 디바이스를 연결해 주세요.",
  llm_login_required: "이 PC에서 AI 계정에 로그인해 주세요.",
  llm_throttled: "AI 한도 복구 시각 이후 자동으로 준비 상태를 확인합니다.",
  unpaired: "담당자에게 PC 연결을 요청해 주세요.",
  paused: "담당자에게 일시 중지 상태를 확인해 주세요.",
  llm_not_installed: "이 PC에 AI 실행 도구를 설치해 주세요.",
};
const aiText = (m: StatusMachine) =>
  m.ai.loginRequired
    ? "로그인 필요"
    : m.ai.throttledUntil
      ? "한도 도달 · 복구 " + time(m.ai.throttledUntil)
      : m.ai.available
        ? "사용 가능"
        : "확인 필요";
const deviceState = (d: Device) =>
  d.state === "device"
    ? "준비됨"
    : d.state === "unauthorized"
      ? "USB 승인 대기"
      : "연결 끊김";
export function networkFixture(
  customer: Customer,
  example: SystemExample = "attention",
  local = false,
  offline = false,
  readiness = "ready",
) {
  const n = example === "empty" ? 0 : local ? 1 : example === "many" ? 12 : 2;
  const machines: StatusMachine[] = Array.from({ length: n }, (_, i) => {
    const disconnected = offline || (example === "attention" && i === 1),
      attention = example === "attention" && i === 0;
    const list: Device[] = Array.from({ length: i === 0 ? 2 : 1 }, (_, j) => ({
      serialTail: String(2041 + i * 10 + j),
      model: j ? "Galaxy A54" : "Galaxy S23",
      state: attention && j === 1 ? "unauthorized" : "device",
      batteryPct: disconnected ? null : j ? 56 : 87,
      charging: disconnected ? null : j === 0,
    }));
    const issues = disconnected
      ? ["offline"]
      : readiness === "ai-limit"
        ? ["llm_throttled"]
        : readiness === "preparing"
          ? ["llm_login_required"]
          : attention
            ? ["device_attention"]
            : [];
    return {
      id: "M-" + i,
      label: local
        ? "이 PC"
        : i === 0
          ? "서울 접수 PC"
          : i === 1
            ? example === "attention"
              ? "서초 PC"
              : "분당 PC"
            : "현장 PC " + (i + 1),
      connected: !disconnected,
      lastSeenAt: stamp(disconnected ? 23.7 : 24),
      version: example === "version-empty" ? null : "1.2.0",
      readiness: issues[0] ?? "ready",
      pendingDeviceCount: attention ? 1 : 0,
      issues,
      ai: {
        available: !["ai-limit", "preparing"].includes(readiness),
        loginRequired: readiness === "preparing",
        throttledUntil: readiness === "ai-limit" ? stamp(27) : null,
      },
      devices: {
        attached: list.length,
        ready: list.filter((d) => d.state === "device").length,
        attention: list.filter((d) => d.state !== "device").length,
        list,
      },
      busy: !disconnected && i === 0,
    };
  });
  const timeline: Timeline = {
    from: stamp(0),
    to: stamp(24),
    hours: 24,
    machines: machines.map((m) => {
      const segments: Segment[] = m.connected
        ? [{ from: stamp(0), to: stamp(24), connected: true }]
        : [
            { from: stamp(0), to: stamp(23.7), connected: true },
            { from: stamp(23.7), to: stamp(24), connected: false },
          ];
      return {
        machineId: m.id,
        label: m.label,
        segments,
        uptimePct: m.connected ? 100 : 98.8,
        disconnects: m.connected ? 0 : 1,
      };
    }),
  };
  return {
    machines,
    timeline,
    attention: machines
      .filter((m) => m.readiness !== "ready")
      .map((m) => ({
        machineId: m.id,
        label: m.label,
        readiness: m.readiness,
        issues: m.issues,
      })),
  };
}
function DeviceChip({
  device,
  stale = false,
}: {
  device: Device;
  stale?: boolean;
}) {
  return (
    <details
      className={
        "ln-device " +
        (device.state === "device" && !stale ? "is-ready" : "is-warning")
      }
    >
      <summary>
        <i />
        <Smartphone size={13} />
        <span>
          {device.model ?? "디바이스"}
          <small>{stale ? "마지막 수신 상태" : deviceState(device)}</small>
        </span>
        <b>
          {device.batteryPct == null ? "—" : device.batteryPct + "%"}
          {device.charging && <BatteryCharging size={13} />}
        </b>
      </summary>
      <p>
        식별번호 끝 {device.serialTail} · {deviceState(device)} ·{" "}
        {device.charging == null
          ? "충전 상태 확인 불가"
          : device.charging
            ? "충전 중"
            : "충전 중 아님"}
        {stale && " · PC 연결 후 갱신"}
      </p>
    </details>
  );
}
export function ConnectionTopology({
  machines,
  onSelect,
  local = false,
  coreConnected = true,
}: {
  machines: StatusMachine[];
  onSelect?: (name: string) => void;
  local?: boolean;
  coreConnected?: boolean;
}) {
  const [expanded, setExpanded] = useState(false),
    visible =
      machines.length >= 10 && !expanded ? machines.slice(0, 4) : machines;
  return (
    <section className="ln-panel" aria-label="연결 구조도">
      <header>
        <div>
          <h3>연결 구조도</h3>
          <p>
            {local ? "코어 ↔ 이 PC → 디바이스" : "코어 → 현장 PC → 디바이스"}
          </p>
        </div>
        <span className="ln-legend">
          <i />
          연결 <i className="broken" />
          확인 필요
        </span>
      </header>
      {!machines.length ? (
        <div className="ln-empty">
          <Monitor size={26} />
          <h4>연결된 머신이 없습니다</h4>
          <p>담당자가 현장 PC를 연결하면 디바이스 상태를 확인할 수 있습니다.</p>
        </div>
      ) : (
        <div className="ln-topology">
          <div className={"ln-core " + (!coreConnected ? "is-offline" : "")}>
            <Cloud size={24} />
            <strong>코어</strong>
            <span>{coreConnected ? "중앙 서버 연결됨" : "다시 연결 중"}</span>
          </div>
          <div className="ln-branches">
            {visible.map((m) => (
              <div
                className={
                  "ln-branch " +
                  (!m.connected || !coreConnected ? "is-offline" : "")
                }
                key={m.id}
              >
                <button
                  className="ln-node"
                  type="button"
                  title={
                    "마지막 수신 " +
                    time(m.lastSeenAt) +
                    (machineVersionLabel(m.version)
                      ? " · " + machineVersionLabel(m.version)
                      : "") +
                    " · AI " +
                    aiText(m)
                  }
                  onClick={() => onSelect?.(m.label)}
                  aria-label={m.label + " 머신 상세"}
                >
                  <Monitor size={19} />
                  <span>
                    <strong>{m.label}</strong>
                    <small>{m.connected ? "연결됨" : "연결 끊김"}</small>
                  </span>
                  <span className="ln-node-meta">
                    {machineVersionLabel(m.version) &&
                      `${machineVersionLabel(m.version)} · `}
                    AI {aiText(m)}
                    <small>마지막 수신 {time(m.lastSeenAt)}</small>
                  </span>
                </button>
                <div className="ln-devices">
                  {m.devices.list.map((d) => (
                    <DeviceChip
                      key={d.serialTail}
                      device={d}
                      stale={!m.connected}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {machines.length >= 10 && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? "대표 머신 4대만 보기"
            : "머신 " + machines.length + "대 모두 보기"}
        </Button>
      )}
    </section>
  );
}
function ConnectionTimeline({ timeline }: { timeline: Timeline }) {
  const total = Date.parse(timeline.to) - Date.parse(timeline.from);
  return (
    <section className="ln-panel" aria-label="24시간 연결 타임라인">
      <header>
        <div>
          <h3>24시간 연결 타임라인</h3>
          <p>
            {time(timeline.from)} — {time(timeline.to)} · KST
          </p>
        </div>
        <span className="ln-legend">
          <i />
          연결 <i className="broken" />
          끊김
        </span>
      </header>
      <div className="ln-timelines">
        {timeline.machines.map((m) => (
          <div className="ln-timeline" key={m.machineId}>
            <span>{m.label}</span>
            <div
              className="ln-track"
              role="img"
              aria-label={
                m.label +
                " 가동률 " +
                m.uptimePct +
                "%, 연결 끊김 " +
                m.disconnects +
                "회"
              }
            >
              {m.segments.map((s, i) => (
                <span
                  key={i}
                  title={
                    time(s.from) +
                    " ~ " +
                    time(s.to) +
                    " · " +
                    (s.connected ? "연결" : "끊김")
                  }
                  style={{
                    left:
                      ((Date.parse(s.from) - Date.parse(timeline.from)) /
                        total) *
                        100 +
                      "%",
                    width:
                      ((Date.parse(s.to) - Date.parse(s.from)) / total) * 100 +
                      "%",
                  }}
                  className={s.connected ? "is-connected" : "is-disconnected"}
                />
              ))}
            </div>
            <strong>{m.uptimePct ?? "—"}%</strong>
          </div>
        ))}
      </div>
      <details className="ln-segments">
        <summary>연결 구간 자세히 보기</summary>
        {timeline.machines.map((m) => (
          <div key={m.machineId}>
            <strong>{m.label}</strong>
            {m.segments.map((s, i) => (
              <p key={i}>
                {time(s.from)} → {time(s.to)} · {s.connected ? "연결" : "끊김"}
              </p>
            ))}
          </div>
        ))}
      </details>
    </section>
  );
}
export function SystemNetwork({
  customer,
  example = "attention",
  onSelect,
  offline = false,
  readiness = "ready",
}: {
  customer: Customer;
  example?: SystemExample;
  onSelect: (name: string) => void;
  offline?: boolean;
  readiness?: string;
}) {
  const data = networkFixture(customer, example, false, offline, readiness),
    { machines, timeline, attention } = data;
  const severe = attention.some((a) =>
    a.issues.some((i) =>
      ["offline", "llm_login_required", "no_device", "unpaired"].includes(i),
    ),
  );
  const state = !machines.length
    ? "연결 대기"
    : severe
      ? "조치 필요"
      : attention.length
        ? "주의"
        : "정상";
  const duration = timeline.machines
      .flatMap((m) => m.segments)
      .reduce((n, s) => n + Date.parse(s.to) - Date.parse(s.from), 0),
    up = timeline.machines
      .flatMap((m) => m.segments)
      .filter((s) => s.connected)
      .reduce((n, s) => n + Date.parse(s.to) - Date.parse(s.from), 0);
  return (
    <div className="ln-stack">
      <div
        className={"ln-health " + (attention.length ? "is-attention" : "")}
        role="status"
      >
        {attention.length ? (
          <AlertCircle size={22} />
        ) : (
          <CheckCircle2 size={22} />
        )}
        <div>
          <strong>{state}</strong>
          <span>
            {!machines.length
              ? "현장 PC 연결을 기다리고 있습니다."
              : attention.length
                ? attention
                    .map(
                      (a) =>
                        a.label +
                        " " +
                        (a.readiness === "offline"
                          ? "18분 전부터 연결 끊김"
                          : a.readiness === "device_attention"
                            ? "USB 승인 필요"
                            : aiText(
                                machines.find((m) => m.id === a.machineId)!,
                              )),
                    )
                    .join(" · ")
                : "모든 PC와 디바이스가 연결되어 있습니다."}
          </span>
          {attention.length > 0 && (
            <p>
              {[...new Set(attention.flatMap((a) => a.issues))]
                .map((i) => issueText[i] ?? "담당자에게 상태를 확인해 주세요.")
                .join(" ")}
            </p>
          )}
        </div>
      </div>
      <SummaryMetrics
        columns={3}
        items={[
          {
            label: "온라인 머신",
            value:
              machines.filter((m) => m.connected).length +
              " / " +
              machines.length,
            icon: <Monitor />,
          },
          {
            label: "준비된 디바이스",
            value:
              machines
                .filter((m) => m.connected)
                .reduce((n, m) => n + m.devices.ready, 0) +
              " / " +
              machines.reduce((n, m) => n + m.devices.attached, 0),
            icon: <Smartphone />,
            note: "연결된 PC의 준비 상태",
            tone: "teal",
          },
          {
            label: "24시간 가동률",
            value: duration ? ((up / duration) * 100).toFixed(1) + "%" : "—",
            icon: <Activity />,
            note: "기록된 머신 연결 시간 기준",
          },
        ]}
      />
      <ConnectionTopology machines={machines} onSelect={onSelect} />
      {machines.length > 0 && (
        <>
          <div className="ln-machine-cards">
            {machines.slice(0, example === "many" ? 4 : undefined).map((m) => (
              <NetworkMachineCard key={m.id} machine={m} onSelect={onSelect} />
            ))}
          </div>
          <ConnectionTimeline timeline={timeline} />
        </>
      )}
    </div>
  );
}

export function NetworkMachineCard({
  machine: m,
  onSelect,
}: {
  machine: StatusMachine;
  onSelect?: (name: string) => void;
}) {
  return (
    <article className="ln-machine">
      <header>
        <button
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(m.label)}
        >
          <Monitor size={16} />
          {m.label}
        </button>
        <Badge tone={m.connected ? "teal" : "coral"}>
          {m.connected ? "연결됨" : "연결 끊김"}
        </Badge>
      </header>
      <MachineDeviceSummary
        deviceCount={m.devices.attached}
        pendingDeviceCount={m.pendingDeviceCount ?? 0}
      />
      <dl>
        <div>
          <dt>마지막 수신</dt>
          <dd>{time(m.lastSeenAt)}</dd>
        </div>
        <div>
          <dt>AI</dt>
          <dd>{aiText(m)}</dd>
        </div>
        <div>
          <dt>프로그램</dt>
          <dd>{machineVersionLabel(m.version) || "—"}</dd>
        </div>
        <div>
          <dt>처리 상태</dt>
          <dd>{!m.connected ? "확인 불가" : m.busy ? "처리 중" : "대기"}</dd>
        </div>
      </dl>
      <div className="ln-chip-list">
        {m.devices.list.map((d) => (
          <DeviceChip key={d.serialTail} device={d} stale={!m.connected} />
        ))}
      </div>
    </article>
  );
}
